import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import apiService from '../services/api';

const ApiTestPage = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({});
  const [error, setError] = useState(null);

  const apiTests = [
    { 
      name: '호스트 정보 조회', 
      function: apiService.getHostInfo,
      description: '호스트 설정 정보 조회'
    },
    { 
      name: 'GPU 목록 조회', 
      function: () => apiService.getGPUs(true),
      description: '호스트의 GPU 목록 조회 (새로고침 적용)'
    },
    { 
      name: 'VM 목록 조회', 
      function: () => apiService.getVMs(true),
      description: '모든 VM 목록 조회'
    },
    { 
      name: 'OS 이미지 목록 조회', 
      function: apiService.getOSImages,
      description: '사용 가능한 OS 이미지 목록 조회'
    },
    { 
      name: '디스크 볼륨 목록 조회', 
      function: apiService.getVolumes,
      description: '디스크 볼륨 목록 조회'
    }
  ];

  const runTest = async (test) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await test.function();
      
      
      setResults(prev => ({
        ...prev,
        [test.name]: {
          success: true,
          data: response.data,
          status: response.status,
          time: new Date().toLocaleTimeString()
        }
      }));
    } catch (err) {
      
      
      setResults(prev => ({
        ...prev,
        [test.name]: {
          success: false,
          error: err.response?.data?.detail || err.message,
          status: err.response?.status,
          time: new Date().toLocaleTimeString()
        }
      }));
      
      setError(`API 연결 오류: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runAllTests = async () => {
    setLoading(true);
    setError(null);
    
    for (const test of apiTests) {
      try {
        const response = await test.function();
        
        
        setResults(prev => ({
          ...prev,
          [test.name]: {
            success: true,
            data: response.data,
            status: response.status,
            time: new Date().toLocaleTimeString()
          }
        }));
      } catch (err) {
        
        
        setResults(prev => ({
          ...prev,
          [test.name]: {
            success: false,
            error: err.response?.data?.detail || err.message,
            status: err.response?.status,
            time: new Date().toLocaleTimeString()
          }
        }));
        
        setError(`API 연결 오류: ${err.message}`);
      }
    }
    
    setLoading(false);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        API 연결 테스트
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" paragraph>
            이 페이지는 백엔드 API 서버와의 연결을 테스트합니다.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={runAllTests}
              disabled={loading}
            >
              모든 API 테스트 실행
            </Button>
            <Button
              variant="outlined"
              onClick={() => setResults({})}
              disabled={loading}
            >
              결과 초기화
            </Button>
          </Box>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Box>
          <Typography variant="h6" gutterBottom>
            개별 API 테스트
          </Typography>
          <List>
            {apiTests.map((test, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1">{test.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{test.description}</Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => runTest(test)}
                      disabled={loading}
                    >
                      테스트
                    </Button>
                  </Box>
                  
                  {results[test.name] && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>
                          {results[test.name].success 
                            ? `✅ 성공 (${results[test.name].status}) - ${results[test.name].time}`
                            : `❌ 실패 (${results[test.name].status || 'N/A'}) - ${results[test.name].time}`}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                          {results[test.name].success ? (
                            <pre style={{ margin: 0 }}>
                              {JSON.stringify(results[test.name].data, null, 2)}
                            </pre>
                          ) : (
                            <Typography color="error">
                              {results[test.name].error}
                            </Typography>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Paper>
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>
    </Box>
  );
};

export default ApiTestPage; 